// src/components/Dialogs/AppTemplateTab.jsx

import React, { useRef, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';

import FormCard from '../Cards/FormCard';
// 1) Import your duplication hook (same as OrganizationTemplateTab)
import { useDuplicateForm } from '../../hooks/useDuplicateForm';
// 2) Import the success modal (same as OrganizationTemplateTab)
import DuplicateSuccessModal from './DuplicateSuccessModal';

/**
 * AppTemplateTab:
 *   - Displays app-wide (global) templates.
 *   - Mirrors OrganizationTemplateTab’s logic but uses "globalTemplates" 
 *     instead of "orgTemplates".
 *   - Infinity scroll, zero-state, duplication, category grouping, etc.
 */
export default function AppTemplateTab({
  // The array of globally published templates
  globalTemplates = [],
  // Whether data is currently loading
  isLoading = false,
  // Whether there are more docs to load (for infinite scroll)
  hasMore = false,
  // Callback to fetch the next batch of data
  fetchNextBatch,
  // Whether the user can submit new global templates
  canPublish = false,
  // Callback to open the SubmitTemplateDialog (for global scope)
  onSubmitTemplateClick,
  // If you have a selected doc (for highlight / config usage)
  selectedDoc,
  onSelectDoc,
  onUnpublishSuccess
}) {
  // Logging input props on every render for debugging
//  console.log('[AppTemplateTab] Rendering with props:', {
//    globalTemplatesLength: globalTemplates.length,
//    isLoading,
//    hasMore,
//    canPublish,
//    selectedDoc,
//  });

  // We'll track if we've done our *first* successful load or final check
  // (i.e., we either got some templates or confirmed none remain)
  const [hasDoneFirstLoad, setHasDoneFirstLoad] = useState(false);

  // For infinite scroll, we use a sentinel ref to detect intersection
  const sentinelRef = useRef(null);

  // ===============================
  // IntersectionObserver for infinite scrolling
  // ===============================
  useEffect(() => {
//    console.log('[AppTemplateTab] useEffect for IntersectionObserver:', {
//      sentinelRefExists: !!sentinelRef.current,
//      hasMore,
//      isLoading,
//      fetchNextBatchExists: !!fetchNextBatch,
//    });

    if (!sentinelRef.current) {
//      console.log('[AppTemplateTab] sentinelRef is not set yet, returning early.');
      return;
    }
    if (!hasMore) {
//      console.log('[AppTemplateTab] hasMore is false, no need to observe intersection for global templates.');
      return;
    }
    if (!fetchNextBatch) {
//      console.log('[AppTemplateTab] fetchNextBatch is not provided, returning early.');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      // Log intersection each time it triggers
//      console.log('[AppTemplateTab] IntersectionObserver entry:', { isIntersecting: entry.isIntersecting });

      if (entry.isIntersecting && !isLoading) {
//        console.log('[AppTemplateTab] Entry is intersecting and not loading. Fetching next batch of global templates.');
        fetchNextBatch();
      }
    }, { rootMargin: '200px' });

    observer.observe(sentinelRef.current);

    return () => {
//      console.log('[AppTemplateTab] Cleaning up IntersectionObserver for global templates.');
      observer.disconnect();
    };
  }, [hasMore, isLoading, fetchNextBatch]);

  // ===============================
  // Check if first load is complete once we finish loading 
  // and see if we got any templates or confirmed none remain
  // ===============================
  useEffect(() => {
//    console.log('[AppTemplateTab] Checking if first load is complete:', {
//      isLoading,
//      globalTemplatesLength: globalTemplates.length,
//      hasMore,
//      hasDoneFirstLoad,
//    });

    // Only mark first load complete if we haven't already
    if (!hasDoneFirstLoad) {
      // If not loading AND (got at least 1 template OR confirmed there's none left)
      if (!isLoading && (globalTemplates.length > 0 || !hasMore)) {
//        console.log('[AppTemplateTab] Marking first load as complete.');
        setHasDoneFirstLoad(true);
      }
    }
  }, [isLoading, globalTemplates, hasMore, hasDoneFirstLoad]);

  // ===============================
  // Group the templates by category
  // ===============================
  const groupedCategories = useMemo(() => {
//    console.log('[AppTemplateTab] useMemo grouping globalTemplates:', globalTemplates);
    if (!globalTemplates.length) {
//      console.log('[AppTemplateTab] globalTemplates is empty, returning [].');
      return [];
    }

    const categoryMap = new Map();
    for (const tpl of globalTemplates) {
      const cat = tpl.templateCategory || 'Uncategorized';
      if (!categoryMap.has(cat)) {
        categoryMap.set(cat, []);
      }
      categoryMap.get(cat).push(tpl);
    }

    const categories = Array.from(categoryMap.keys()).sort((a, b) => a.localeCompare(b));
//   console.log('[AppTemplateTab] Sorted categories:', categories);

    return categories.map((cat) => ({
      category: cat,
      forms: categoryMap.get(cat),
    }));
  }, [globalTemplates]);

  const totalTemplates = globalTemplates.length;

  /**
   * We introduce 'allLoading' to represent any scenario
   * in which the user should *not* see zero-state or final templates yet.
   *
   * - If we're currently loading (isLoading = true), *or*
   * - We haven't done our *first* load yet (hasDoneFirstLoad = false).
   */
  const allLoading = isLoading || !hasDoneFirstLoad;

  // Show zero-state only if not loading, we've finished first load, and we have no templates.
  const showZeroState = !allLoading && totalTemplates === 0;

//  console.log('[AppTemplateTab] Computed values:', {
//    totalTemplates,
//    showZeroState,
//    isLoading,
//    hasDoneFirstLoad,
//    allLoading
//  });

  // ===============================
  // Use the duplication hook here
  // ===============================
  const {
    handleDuplicateForm,
    duplicateModalState,
    dismissDuplicateModal,
    goToDuplicatedForm,
  } = useDuplicateForm();

  // Callback that triggers duplication when user clicks “Configure”
  const onConfigureTemplate = (tplObj) => {
//    console.log('[AppTemplateTab] onConfigureTemplate called with:', { tplObjId: tplObj?.id });
    if (!tplObj?.id) return;
    handleDuplicateForm(tplObj.id);
  };

  // ===============================
  // Render the component
  // ===============================
  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: 8,
        py: 3,
        width: '100%',
      }}
    >
      {console.log('[AppTemplateTab] Rendering top bar with totalTemplates:', totalTemplates, 'canPublish:', canPublish)}

      {/* 
        If we already have templates loaded, show "Submit template" button 
        for users who can publish at global scope
      */}
      {totalTemplates > 0 && canPublish && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: 0
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={onSubmitTemplateClick}
            sx={{
              textTransform: 'none',
              minWidth: 160,
              backgroundColor: '#fff',
              boxShadow: (theme) => theme.shadows[2],
            }}
          >
            Submit template
          </Button>
        </Box>
      )}

      {/* 
        1) If we're loading or haven't finished the first load => spinner 
           (only if you want a spinner during the entire first load)
      */}
      {allLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, minHeight: '300px' }}>
          {console.log('[AppTemplateTab] allLoading is true => showing spinner.')}
          <CircularProgress size={32} />
        </Box>
      )}

      {/* 
        2) If not loading, we've done the first load, and have no templates => zero state 
      */}
      {showZeroState && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 48px)',
            textAlign: 'center',
          }}
        >
          {console.log('[AppTemplateTab] Showing zero-state UI for global templates.')}
          <DescriptionIcon
            sx={{
              fontSize: 80,
              color: 'grey.500',
              mb: 3,
            }}
          />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 'medium' }}>
            No templates available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            It looks like there aren&apos;t any global templates yet. 
            Be the first to submit one.
          </Typography>

          {canPublish && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={onSubmitTemplateClick}
              sx={{
                textTransform: 'none',
                minWidth: 160,
                backgroundColor: '#fff',
                boxShadow: (theme) => theme.shadows[2],
              }}
            >
              Submit template
            </Button>
          )}
        </Box>
      )}

      {/* 
        3) If not allLoading and we have templates => show them 
      */}
      {!allLoading && totalTemplates > 0 && (
        <>
          {groupedCategories.map(({ category, forms }) => {
            console.log('[AppTemplateTab] Rendering category:', category, 'with', forms.length, 'forms');
            return (
              <Box key={category} sx={{ mb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 'medium',
                    color: 'text.secondary'
                  }}
                >
                  {category}
                </Typography>

                <Grid
                  container
                  spacing={4}
                  wrap="nowrap"
                  sx={{ overflowX: 'auto' }}
                >
                  {forms.map((tplObj) => {
                    const docId = tplObj.id;
                    const isSelected = selectedDoc?.id === docId;

                    console.log('[AppTemplateTab] Rendering FormCard for docId:', docId, 'isSelected:', isSelected);

                    return (
                      <Grid
                        item
                        key={docId}
                        sx={{ flex: '0 0 auto' }}
                      >
                        <FormCard
                          form={tplObj}
                          onClick={() => onSelectDoc?.(tplObj)} // if you want to highlight/select
                          isSelected={isSelected}
                          showOverflowMenu
                          // The two props that enable duplication logic for the gallery
                          isTemplateInGallery
                          onConfigureTemplate={onConfigureTemplate}
                          templateScope="global"
                          onUnpublishSuccess={onUnpublishSuccess}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </>
      )}

      {/* If loading after first load, we can display a smaller spinner for infinite scroll. */}
      {!allLoading && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          {console.log('[AppTemplateTab] Showing smaller loading spinner for subsequent loads.')}
          <CircularProgress size={24} />
        </Box>
      )}

      {/* The intersection observer sentinel (1px box) for infinite scrolling */}
      <Box ref={sentinelRef} sx={{ height: '1px', width: '100%', mt: 2 }} />

      {console.log('[AppTemplateTab] DuplicateSuccessModal state:', duplicateModalState)}
      <DuplicateSuccessModal
        open={duplicateModalState.open}
        onClose={dismissDuplicateModal}
        onNavigate={goToDuplicatedForm}
      />
    </Box>
  );
}
